import {
  type CarePlanAdl,
  type Carer,
  CommonErrors,
  type GoalCarePlanResponse,
  type Resident
} from '@lmc/ts-common'
import { postJSON } from 'common/services/api'
import { getResidentPronouns } from 'common/utils'
import { getCarer, getCarerName } from 'hooks/useFindItem/useCarers'
import type { TFunction } from 'i18next'
import { goalsConstants } from 'locale/goals'
import getInUseField from 'utils/getInUseField/getInUseField'
import { dataURItoBlob } from '../ResidentConsentScreens/utils'
import type { Plan } from './components/SupportPlanInput/CarePlanInput'

export type FormState = {
  ability: boolean
  capacity: boolean
  name: string
  relationship: string
  imageURL: string
  hasConfirmed: boolean
  lpaValue: any
  comment: string
  reason: string
}

export const generateLogText = (
  formState: FormState,
  resident: Resident,
  goalTitle: string
) => {
  const { capacity, ability, comment, reason, name, relationship } = formState

  const residentName = resident?.preferredName || resident.name.first
  const { subject: pronoun, verb } = getResidentPronouns(resident)

  let title: string
  let description: string

  if (capacity) {
    if (ability) {
      title = `Signed by ${residentName}`
      description = `${residentName} confirms that ${pronoun} ${verb('approve')} the ${goalTitle} .`
    } else {
      title = `Signed by ${name}`
      description = `${residentName} confirms that ${pronoun} ${verb('approve')} the ${goalTitle}. Witnessed and signed by ${name} (${relationship}).`
    }
  } else {
    title = `Signed by ${name}`
    description = `${name} (${relationship}) signed the ${goalTitle} on behalf of ${residentName}.`
  }

  if (reason) description += ` ${residentName} did not sign because: ${reason}.`
  if (comment) description += ` ${comment}.`

  return { title, description }
}

// @ts-ignore
export const onSubmit = async ({
  formState,
  goalId,
  resident,
  onSuccess,
  onError,
  category = '5e867702b349f776c8d48c2f',
  item = '61794b0a988b150f6ebdac37',
  goalTitle
}: any) => {
  try {
    const { title, description } = generateLogText(
      formState,
      resident,
      goalTitle
    )
    const { imageURL } = formState
    const log = {
      resident: resident.id,
      title,
      description,
      goalId, // should be the permanent ID, not the objectID
      hidden: true,
      isCustomEntry: true,
      category,
      item
    }

    const res = await postJSON({
      url: 'list/logs',
      json: log
    })

    // The Create Log endpoint can't handle pictures
    // So we need to use the PUT request to upload a photo
    // TODO: refactor and merge the requests!
    // eslint-disable-next-line prefer-const
    const form = new FormData()
    const file = dataURItoBlob(imageURL)
    // @ts-ignore
    form.append('picture1', file)

    await postJSON({
      url: `list/logs/${res.result.id}`,
      method: 'PUT',
      contentType: 'multipart/form-data',
      body: form // log,
    })

    await postJSON({
      url: `list/residents/${resident?.id}/goals/${goalId}/sign`,
      json: { id: res.result.id }
    })
    onSuccess()
  } catch (e) {
    onError(e)
  }
}

export type CarePlansInputProps = {
  disabled: boolean
  value?: Plan[]
  onChange?: (selectedCarePlans: Plan[]) => void
  testId?: string
}

export const transformCarePlanAdlIntoPlan = (
  carePlanAdl: CarePlanAdl | undefined | null,
  isPDF?: boolean
): Pick<Plan, 'title' | 'icon' | 'iconColor'> => ({
  title: carePlanAdl?.name,
  icon: isPDF ? null : (carePlanAdl?.icon as any),
  iconColor: isPDF ? 'transparent' : carePlanAdl?.color
})

export const transformGoalCarePlanIntoPlan = (
  carePlans: GoalCarePlanResponse[] | undefined,
  carers: Carer[],
  isPDF?: boolean
): CarePlansInputProps['value'] => {
  if (!carePlans) {
    return []
  }

  const plan: CarePlansInputProps['value'] = carePlans.map((carePlan) => {
    const linkedByCarer = getCarer(carers, carePlan.createdBy)
    const linkedBy = getCarerName(linkedByCarer)

    return {
      ...carePlan.carePlanAdl,
      // @ts-ignore
      ...transformCarePlanAdlIntoPlan(carePlan.carePlanAdl, isPDF),
      id: carePlan.id,
      linkedDateTime: carePlan.createdOn,
      linkedBy,
      riskAssesmentsIDs: carePlan.riskAssessments,
      nextReviewDate: carePlan.nextReviewDate
    }
  })

  return plan
}

export const getToastError = (
  err: CommonErrors.Dto,
  t: TFunction<string, undefined>
): string => {
  const alreadyInUseFieldName = getInUseField(err.message)

  if (err.code === CommonErrors.Codes.badRequest || alreadyInUseFieldName) {
    if (alreadyInUseFieldName === 'title') {
      return t(goalsConstants.TITLE_IN_USE_ERROR)
    }
  }

  return t(goalsConstants.ERROR)
}

export const hasActionPlanTitleError = (value: string) => {
  if (value === '') {
    return true
  }

  return false
}
